import React, { createContext, useContext, useEffect, useState } from 'react';
import { providerApi } from '../services/api';

const CLOSED = Object.freeze({
  paymentsEnabled: false,
  bookingSettlementOffline: false,
  servicePaymentsEnabled: false,
  storePaymentsEnabled: false,
});

/**
 * Mirrors the Flutter PaymentsConfig exactly: strict booleans, fail closed.
 *  1. null / non-object body (and, in the provider, any request failure) -> everything false.
 *  2. A per-type field the server sends is used if it is a boolean, else false.
 *  3. Absent per-type field (older backend) -> derived from the legacy fields, but only when
 *     those are booleans: service = paymentsEnabled && !bookingSettlementOffline,
 *     store = paymentsEnabled.
 */
export function resolveRuntimeConfig(cfg) {
  if (!cfg || typeof cfg !== 'object') return { ...CLOSED };
  const { paymentsEnabled: pe, bookingSettlementOffline: off } = cfg;
  const perType = (key, legacy) => (key in cfg ? cfg[key] === true : legacy);
  return {
    paymentsEnabled: pe === true,
    bookingSettlementOffline: off === true,
    servicePaymentsEnabled: perType(
      'servicePaymentsEnabled',
      typeof pe === 'boolean' && typeof off === 'boolean' && pe && !off,
    ),
    storePaymentsEnabled: perType('storePaymentsEnabled', typeof pe === 'boolean' && pe),
  };
}

/**
 * The server's effective payment switches, read once at load, fail-closed. Never a build-time
 * flag — the backend's 503 gates win regardless.
 */
const RuntimeConfigContext = createContext({ ...CLOSED, loaded: false });

export const useRuntimeConfig = () => useContext(RuntimeConfigContext);

export const RuntimeConfigProvider = ({ children }) => {
  const [state, setState] = useState({ ...CLOSED, loaded: false });
  useEffect(() => {
    let cancelled = false;
    providerApi.getRuntimeConfig()
      .then((cfg) => { if (!cancelled) setState({ ...resolveRuntimeConfig(cfg), loaded: true }); })
      .catch(() => { if (!cancelled) setState({ ...CLOSED, loaded: true }); });
    return () => { cancelled = true; };
  }, []);
  return <RuntimeConfigContext.Provider value={state}>{children}</RuntimeConfigContext.Provider>;
};
